import { getAllTokenData } from "utils/token-helper"
import { SettingConstant } from "shared/constants/setting-constant"
import { sendExpressLog, shipment } from "shared/constants/endpoint"
import { storageName } from "shared/constants/storage-constant"
import * as StorageHelper from "utils/storage-helper"
import { LoggingHelper } from "utils/logging-helper"
import { getAnalyticsContext } from "utils/analytics"

const { fetch: originalFetch } = window

const getRequestUrl = resource => {
  if (typeof resource === "string") return resource
  if (resource && typeof resource.url === "string") return resource.url
  return ""
}

const getRequestMethod = (resource, options) => {
  const method = options?.method || resource?.method || "GET"
  return method.toUpperCase()
}

const getRequestHeader = (resource, options, key) => {
  if (options?.headers?.[key]) return options.headers[key]
  if (resource?.headers?.get) return resource.headers.get(key)
  return undefined
}

const getRequestPayload = options => {
  const body = options?.body
  if (body == null) return null

  if (typeof body === "string") {
    try {
      return JSON.parse(body)
    } catch (error) {
      return body
    }
  }

  if (body instanceof URLSearchParams) {
    return body.toString()
  }

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    return Object.fromEntries(body.entries())
  }

  return body
}

const isEventLogRequest = resource => {
  const eventLogUrl = sendExpressLog
  const requestUrl = getRequestUrl(resource)

  if (!eventLogUrl || !requestUrl) return false
  return requestUrl.includes(eventLogUrl)
}

const isSetOnOrOffHoldRequest = resource => {
  const requestUrl = getRequestUrl(resource)
  if (!requestUrl) return false

  const normalizedRequestUrl = requestUrl.toLowerCase()
  const endpointPath = "set-on-off-hold"
  const endpointPathAlt = "setonoroffhold"

  if (normalizedRequestUrl.includes(endpointPath) || normalizedRequestUrl.includes(endpointPathAlt)) return true

  try {
    const parsedUrl = new URL(requestUrl, window.location.origin)
    const pathname = parsedUrl.pathname.toLowerCase()
    const configuredPath = new URL(shipment.setOnOrOffHold, window.location.origin).pathname.toLowerCase()

    return pathname === configuredPath || pathname.endsWith(`/${endpointPath}`) || pathname.endsWith(`/${endpointPathAlt}`)
  } catch (error) {
    return false
  }
}

const sendEventLog = async (resource, options, payload) => {
  const eventLogUrl = sendExpressLog
  if (!eventLogUrl) return

  const headers = {
    "Content-Type": "application/json; charset=utf-8",
    "Ocp-Apim-Subscription-Key": SettingConstant.SUBCRIPTION_KEY,
  }

  const authorization = getRequestHeader(resource, options, "Authorization")
  if (!authorization) return

  headers["Authorization"] = authorization

  const company = getRequestHeader(resource, options, "Company") || getRequestHeader(resource, options, "X-Company-Code")
  if (company) {
    headers["Company"] = company
    headers["X-Company-Code"] = company
    headers["Company-Code"] = company
  }

  try {
    await originalFetch(eventLogUrl, {
      method: "POST",
      headers,
      keepalive: true,
      body: JSON.stringify(payload),
    })
  } catch (error) {
    LoggingHelper.writeLog("http-service", "sendEventLog", `Failed to send event log: ${error?.message || error}`)
  }
}

const getResponseData = async response => {
  try {
    const responseClone = response.clone()
    const contentType = responseClone.headers?.get("content-type") || ""

    if (contentType.includes("application/json")) {
      return await responseClone.json()
    }

    const text = await responseClone.text()
    return text || null
  } catch (error) {
    return null
  }
}

window.fetch = async (...args) => {
  const [resource, options] = args
  const method = getRequestMethod(resource, options)
  const requestUrl = getRequestUrl(resource)
  const isSetOnOrOffHold = isSetOnOrOffHoldRequest(resource)
  const shouldLogEvent = SettingConstant.SHOULD_LOG_EVENTS && (method !== "GET" || isSetOnOrOffHold) && !isEventLogRequest(resource)
  const startTime = Date.now()

  LoggingHelper.writeLog("http-service", "Request", `--> ${method} ${requestUrl || resource} (body: ${options?.body})`)

  try {
    const response = await originalFetch(resource, options)

    if (shouldLogEvent) {
      const logTask = (async () => {
        const payload = {
          ID: new Date().getTime(),
          timestamp: new Date().toISOString(),
          method,
          url: requestUrl,
          requestPayload: getRequestPayload(options),
          status: response.status,
          durationMs: Date.now() - startTime,
          result: await getResponseData(response),
          company: StorageHelper.getStorage(storageName.activeCompany),
          user: StorageHelper.getStorage(storageName.user),
          // userId / isInternal / appVersion / route: join to GA and filter out eStore staff
          ...getAnalyticsContext(),
        }

        await sendEventLog(resource, options, payload)
      })()

      if (isSetOnOrOffHold) {
        await logTask
      } else {
        void logTask
      }
    }

    return response
  } catch (error) {
    if (shouldLogEvent) {
      const logTask = (async () => {
        const payload = {
          ID: new Date().getTime(),
          timestamp: new Date().toISOString(),
          method,
          url: requestUrl,
          requestPayload: getRequestPayload(options),
          status: 0,
          durationMs: Date.now() - startTime,
          result: "failed",
          error: error?.message || "network_error",
          company: StorageHelper.getStorage(storageName.activeCompany),
          user: StorageHelper.getStorage(storageName.user),
          // userId / isInternal / appVersion / route: join to GA and filter out eStore staff
          ...getAnalyticsContext(),
        }

        await sendEventLog(resource, options, payload)
      })()

      if (isSetOnOrOffHold) {
        await logTask
      } else {
        void logTask
      }
    }

    throw error
  }
}

class HttpService {
  buildConfig(configCustom, method, data) {
    try {
      const headers = {
        "Content-Type": "application/json; charset=utf-8",
        "Ocp-Apim-Subscription-Key": SettingConstant.SUBCRIPTION_KEY,
      }

      const tokenData = getAllTokenData()
      const token = tokenData ? tokenData.access_token : ""

      if (token) {
        headers["Authorization"] = `Bearer ${token}`

        const company = StorageHelper.getStorage(storageName.activeCompany)
        if (company) {
          headers["Company"] = company
          headers["X-Company-Code"] = company
          headers["Company-Code"] = company
        }
      }

      const config = {
        method: method || "get",
        headers: headers,
      }

      if (data) {
        config["body"] = JSON.stringify(data)
      }

      return configCustom ? { ...config, configCustom } : config
    } catch (error) {
      throw new Error(error)
    }
  }

  async handleResult(result) {
    if (result.status == 401 || result.status == 403) {
      //this.forceAuthentication()
      return result.status
    }

    if (result.status >= 500 && result.status < 600) return { success: false, error: "Server error, please try again later." }

    try {
      const res = await result.json()
      if (res.statusCode == 404) return null
      return res
    } catch (e) {
      if (result.status >= 200 && result.status < 300) return null
      throw e
    }
  }

  async get(url, config) {
    try {
      const results = await fetch(url, this.buildConfig(config, "get"))
      return await this.handleResult(results)
    } catch (error) {
      return this.handleError(error)
    }
  }

  async post(url, data, config) {
    try {
      const results = await fetch(url, this.buildConfig(config, "post", data))
      return await this.handleResult(results)
    } catch (error) {
      return this.handleError(error)
    }
  }

  async put(url, data, config) {
    try {
      const results = await fetch(url, this.buildConfig(config, "put", data))
      return await this.handleResult(results)
    } catch (error) {
      return this.handleError(error)
    }
  }

  async patch(url, data, config) {
    try {
      const results = await fetch(url, this.buildConfig(config, "PATCH", data))
      return await this.handleResult(results)
    } catch (error) {
      return this.handleError(error)
    }
  }

  async delete(url, config) {
    try {
      const results = await fetch(url, this.buildConfig(config, "delete"))
      return await this.handleResult(results)
    } catch (error) {
      return this.handleError(error)
    }
  }

  forceAuthentication() {
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.set("unauthorized", "true")
    window.location.search = urlParams
  }

  handleError(error) {
    //console.log(error)

    this.forceAuthentication()

    return Promise.resolve({ success: false, error: error })
  }
}
const httpService = new HttpService()
export { httpService }
